import { z } from 'zod'

/**
 * Wire shape of bristol's `/cockpit/data` endpoint: a raw Metabase dashboard
 * export (dashboard 593, instance `main`), not a pre-shaped view model.
 * Field names stay snake_case (and some are quoted, mirroring Metabase's own
 * `last-value`/`previous-value` keys) because this schema describes the API
 * contract verbatim — renaming happens in the view-model transform, not here.
 *
 * Deliberately only declares the fields we actually read. Zod's default
 * object parsing strips unknown keys, so Metabase adding fields elsewhere in
 * `query_result` (there are ~10 more we don't use: `json_query`,
 * `native_form`, `results_metadata.columns[].fingerprint`, etc.) won't break
 * this schema.
 */

const rowCellSchema = z.union([z.string(), z.number(), z.null()])

const colSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  base_type: z.string(),
})

// `semantic_type` only exists here, not on `cols` — index-aligned with it.
const metadataColumnSchema = z.object({
  name: z.string(),
  semantic_type: z.string().nullable(),
})

const insightSchema = z.object({
  col: z.string(),
  unit: z.string().nullable(),
  'last-value': z.number().nullable(),
  'previous-value': z.number().nullable(),
  'last-change': z.number().nullable(),
})

const cardSchema = z.object({
  dashcard_id: z.number(),
  card_id: z.number(),
  name: z.string(),
  // Not an enum: an unrecognized Metabase display type must not hard-fail
  // the whole dashboard fetch.
  display: z.string(),
  query_result: z.object({
    started_at: z.string(),
    row_count: z.number(),
    data: z.object({
      rows: z.array(z.array(rowCellSchema)),
      cols: z.array(colSchema),
      results_metadata: z.object({ columns: z.array(metadataColumnSchema) }),
      insights: z.array(insightSchema).nullable(),
    }),
  }),
})

export const metabaseDashboardSchema = z.object({
  dashboard_id: z.number(),
  instance: z.string(),
  structure: z.object({
    name: z.string(),
  }),
  cards: z.array(cardSchema),
})

export type MetabaseDashboard = z.infer<typeof metabaseDashboardSchema>
